/**
 * 開一張銷貨折讓單(REQ-20260821001)。
 *
 * 需求:「跨月改帳時原帳不動,直接開一筆折讓單記錄要退給客戶的金額,
 * 下個月產生月結對帳單時自動在總額中扣除。」
 *
 * 所以這裡**不動出貨單本身**:折讓單是另一份憑證,只影響對帳單那一期的
 * 「本期折讓」。單一訂單一次性的折讓仍然走既有的 `allowanceAmount`
 * (兩者的分工見後端 `credit-notes.service.ts` 檔頭)。
 *
 * 金額由後端驗證(折讓合計不可超過單據總額),這裡的顯示只是讓使用者
 * 按下去之前就看得到已經開過多少。
 */
import { useState } from 'react';
import type { CreateSalesOrderCreditNoteInput } from '@app/shared';
import { Modal } from '../../components/Modal';
import { getApiErrorMessage } from '../../lib/api-client';
import { formatMoney } from '../../lib/format-money';
import { useAuthStore } from '../auth/store';
import { useCreateCreditNote, useOrderCreditNotes, useReviewCreditNote } from '../accounting/api';

export interface CreditNoteModalProps {
  open: boolean;
  onClose: () => void;
  orderId: number;
  orderNo: string;
  /** 單據含稅總計 —— 顯示在核對區塊,同時作為「已開折讓 / 單據總額」的對照 */
  orderTotal: string;
  /**
   * 核對用的單據資訊(REQ-20260821002)。全部來自呼叫端手上的 `SalesOrderListItem`,
   * **沒有多打一次 API** —— 只有一組單號的畫面讓人不確定改的是不是那張單。
   */
  customerName: string;
  txnDate: string;
  firstProductName?: string | null | undefined;
  lineCount: number;
  onCreated: (documentNo: string) => void;
}

export function CreditNoteModal({
  open,
  onClose,
  orderId,
  orderNo,
  orderTotal,
  customerName,
  txnDate,
  firstProductName,
  lineCount,
  onCreated,
}: CreditNoteModalProps): JSX.Element {
  const existing = useOrderCreditNotes(open ? orderId : undefined);
  const mutation = useCreateCreditNote(orderId);
  /**
   * 審核是**會計 / 主管專用**(比照 REQ-20260821001 解除沖銷)。前端只是不給入口 ——
   * 後端 `POST /credit-notes/:id/review` 掛 `@Roles('ADMIN')`,那才是權威。
   */
  const isAdmin = useAuthStore((s) => s.user?.role === 'ADMIN');
  const reviewMutation = useReviewCreditNote();
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [issuedDate, setIssuedDate] = useState(todayInTaipei);
  /** 空字串 = 不指定 = 後端自動套用到開立日的次月(需求原文的「下個月」) */
  const [appliedPeriod, setAppliedPeriod] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = (): void => {
    if (!(Number(amount) > 0)) {
      setError('請填寫大於 0 的折讓金額');
      return;
    }
    if (!reason.trim()) {
      setError('請填寫折讓原因');
      return;
    }
    const input: CreateSalesOrderCreditNoteInput = {
      amount: amount.trim(),
      reason: reason.trim(),
      issuedDate,
      appliedPeriod: appliedPeriod.trim() || null,
    };
    mutation.mutate(input, {
      onSuccess: (note) => {
        setAmount('');
        setReason('');
        setAppliedPeriod('');
        setError(null);
        onCreated(note.documentNo);
      },
      // 超過單據總額 / 草稿單不能開,後端訊息本身就是下一步指示,原話透出
      onError: (err) => setError(getApiErrorMessage(err, '開立折讓單失敗')),
    });
  };

  const review = (creditNoteId: number): void => {
    reviewMutation.mutate(creditNoteId, {
      onSuccess: () => setError(null),
      onError: (err) => setError(getApiErrorMessage(err, '審核折讓單失敗')),
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`開折讓單 — ${orderNo}`}
      dismissOnBackdrop={false}
      widthClass="max-w-2xl"
    >
      <div className="grid gap-4">
        <p className="text-sm text-slate-600">
          折讓單<strong>不會改動這張出貨單</strong>,只會在套用期別的月結對帳單裡從總額扣除,
          而且<strong>要經過審核才生效</strong>。單據含稅總計 {formatMoney(orderTotal)},已開折讓{' '}
          {formatMoney(existing.data?.totalAmount ?? '0')}。
        </p>

        {/* 核對區塊(REQ-20260821002):送出前先確認這是不是要折讓的那一張單 */}
        <dl
          data-testid="credit-note-order-context"
          className="grid gap-x-6 gap-y-1 sm:grid-cols-2 rounded-md border bg-slate-50 px-4 py-3 text-sm"
        >
          <div className="flex gap-2">
            <dt className="text-slate-500 shrink-0">客戶</dt>
            <dd className="font-medium truncate">{customerName}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="text-slate-500 shrink-0">出貨日期</dt>
            <dd className="font-mono">{txnDate}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="text-slate-500 shrink-0">品名</dt>
            <dd className="truncate">
              {firstProductName
                ? `${firstProductName}${lineCount > 1 ? ` 等${lineCount}項` : ''}`
                : `共 ${lineCount} 項`}
            </dd>
          </div>
          <div className="flex gap-2">
            <dt className="text-slate-500 shrink-0">單據含稅總計</dt>
            <dd className="font-mono">{formatMoney(orderTotal)}</dd>
          </div>
        </dl>

        <div className="grid gap-3 md:grid-cols-4">
          <div className="grid gap-1">
            <label className="text-sm text-slate-600" htmlFor="credit-note-amount">
              折讓金額
            </label>
            <input
              id="credit-note-amount"
              type="text"
              inputMode="decimal"
              data-testid="credit-note-amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="border rounded-md px-2 py-1.5 text-sm text-right font-mono"
            />
          </div>
          <div className="grid gap-1">
            <label className="text-sm text-slate-600" htmlFor="credit-note-issued-at">
              開立日期
            </label>
            <input
              id="credit-note-issued-at"
              type="date"
              data-testid="credit-note-issued-at"
              value={issuedDate}
              onChange={(e) => setIssuedDate(e.target.value)}
              className="border rounded-md px-2 py-1.5 text-sm"
            />
          </div>
          <div className="grid gap-1">
            <label className="text-sm text-slate-600" htmlFor="credit-note-period">
              套用期別
            </label>
            <input
              id="credit-note-period"
              type="month"
              data-testid="credit-note-period"
              value={appliedPeriod}
              onChange={(e) => setAppliedPeriod(e.target.value)}
              className="border rounded-md px-2 py-1.5 text-sm"
            />
            <span className="text-xs text-slate-400">留空 = 下個月的對帳單</span>
          </div>
          <div className="grid gap-1 md:col-span-4">
            <label className="text-sm text-slate-600" htmlFor="credit-note-reason">
              折讓原因
            </label>
            <input
              id="credit-note-reason"
              type="text"
              data-testid="credit-note-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="border rounded-md px-2 py-1.5 text-sm"
            />
          </div>
        </div>

        {/* 已開過的折讓單:載入 / 空狀態沿用 ui-guidelines §4 */}
        {existing.isLoading ? (
          <div role="status" className="text-slate-500 text-sm">
            載入已開折讓單...
          </div>
        ) : (existing.data?.items ?? []).length === 0 ? (
          <div className="text-slate-500 text-sm">這張單還沒有開過折讓單。</div>
        ) : (
          <table className="w-full bg-white shadow-sm rounded-md">
            <thead>
              <tr className="border-b text-left text-sm text-slate-600">
                <th className="px-4 py-2">折讓單號</th>
                <th className="px-4 py-2">開立日期</th>
                <th className="px-4 py-2">套用期別</th>
                <th className="px-4 py-2">原因</th>
                <th className="px-4 py-2 text-right">金額</th>
                <th className="px-4 py-2">狀態</th>
              </tr>
            </thead>
            <tbody>
              {(existing.data?.items ?? []).map((c) => (
                <tr key={c.id} className="border-b last:border-0 hover:bg-slate-50 text-sm">
                  <td className="px-4 py-2 whitespace-nowrap font-mono">{c.documentNo}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{c.issuedDate}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{c.effectivePeriod}</td>
                  <td className="px-4 py-2 max-w-xs truncate">{c.reason}</td>
                  <td className="px-4 py-2 text-right font-mono">{formatMoney(c.amount)}</td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    {c.status === 'PENDING' ? (
                      <span className="flex items-center gap-2">
                        <span className="text-amber-700">待審核</span>
                        {isAdmin ? (
                          <button
                            type="button"
                            disabled={reviewMutation.isPending}
                            onClick={() => review(c.id)}
                            data-testid={`credit-note-review-${c.id}`}
                            className="text-blue-600 hover:underline disabled:opacity-50"
                          >
                            審核
                          </button>
                        ) : null}
                      </span>
                    ) : (
                      <span
                        className="text-slate-600"
                        title={
                          c.reviewedByName ? `審核人:${c.reviewedByName}` : undefined
                        }
                      >
                        已審核
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {error ? (
          <div
            role="alert"
            className="text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2 text-sm"
            data-testid="credit-note-error"
          >
            {error}
          </div>
        ) : null}

        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="text-slate-600 hover:underline">
            取消
          </button>
          <button
            type="button"
            disabled={mutation.isPending}
            onClick={submit}
            data-testid="credit-note-submit"
            className="bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            開立折讓單
          </button>
        </div>
      </div>
    </Modal>
  );
}

/** 預設開立日 = 台北今天(瀏覽器時區不影響,同 ReceiptModal) */
function todayInTaipei(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Taipei',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}
